import axios from 'axios';
import {
   DashboardData,
   RecentResult,
   ProcessingSummary,
   ApiResponse,
   ApiListResponse,
  ResultsListResponse,
  FilterOptions,
  SortOptions,
  ResultSummary,
  ExportOptions,
  BulkOperation,
  EvaluationQuestion,
  ParameterAssignment,
  EvaluationStatus,
  QueuedEvaluation,
  RegistrationCheckResponse,
  RegistrationResolveRequest,
  DrawingStatusResponse,
  CandidateConflictsResponse,
  ConflictResolutionRequest,
  CandidateRegistrationCheckResponse
} from '../types/api';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: (import.meta as any).env?.VITE_API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Initialize auth token from localStorage on app start
const token = localStorage.getItem('sessionToken');
if (token) {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Response Error:', error);

    if (error.response) {
      const message = error.response.data?.message || error.response.data?.error || 'Server error';
      throw new Error(`${error.response.status}: ${message}`);
    } else if (error.request) {
      throw new Error('Network error: Unable to connect to server');
    } else {
      throw new Error(`Request error: ${error.message}`);
    }
  }
);

// Dashboard API endpoints
export const dashboardApi = {
  getStatistics: async (): Promise<DashboardData> => {
    const response = await api.get<ApiResponse<DashboardData>>('/dashboard/statistics');
    return response.data.data;
  },

  getRecentResults: async (limit: number = 10): Promise<RecentResult[]> => {
    const response = await api.get<ApiListResponse<RecentResult>>('/dashboard/recent-results', {
      params: { limit },
    });
    return response.data.data;
  },

  getProcessingSummary: async (): Promise<ProcessingSummary> => {
    const response = await api.get<ApiResponse<ProcessingSummary>>('/dashboard/processing-summary');
    return response.data.data;
  },
};

// Processing API endpoints
export const processingApi = {
  startProcessing: async (): Promise<{ message: string; started: boolean; timestamp: string }> => {
    const response = await api.post<ApiResponse<{ message: string; started: boolean; timestamp: string }>>('/processing/start');
    return response.data.data;
  },

  getStatus: async (): Promise<any> => {
    const response = await api.get<ApiResponse<any>>('/processing/status');
    return response.data.data;
  },

  stopProcessing: async (): Promise<{ message: string }> => {
    const response = await api.post<ApiResponse<{ message: string }>>('/processing/stop');
    return response.data.data;
  },
};

// System API endpoints
export const systemApi = {
  getHealth: async (): Promise<any> => {
    const response = await api.get('/system/health');
    return response.data;
  },

  getMetrics: async (): Promise<any> => {
    const response = await api.get<ApiResponse<any>>('/system/metrics');
    return response.data.data;
  },
};

// Results API endpoints
export const resultsApi = {
  // Get results with filtering, sorting, and pagination
  // UPDATED BY SAMIKSHA: Always returns candidate-wise results showing overall scores out of 100
  getResults: async (params: {
    page?: number;
    limit?: number;
    filters?: FilterOptions;
    sort?: SortOptions;
  } = {}): Promise<ResultsListResponse> => {
    const queryParams = new URLSearchParams();

    // Pagination
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    // Filters
    // FIX: skip 'all', empty string, null, undefined — backend doesn't understand 'all'
    if (params.filters) {
      Object.entries(params.filters).forEach(([key, value]) => {
        if (
          value !== undefined &&
          value !== null &&
          value !== '' &&
          value !== 'all'
        ) {
          // FIX: map verificationStatus → isVerified boolean for backend compatibility
          // Change the block below based on what your backend actually expects:
          //   • If backend expects verificationStatus=verified / verificationStatus=not_verified → keep as-is
          //   • If backend expects isVerified=true / isVerified=false → use the mapping below
          if (key === 'verificationStatus') {
            if (value === 'verified') {
              // Try BOTH field names — remove whichever your backend does NOT use
              queryParams.append('verificationStatus', 'verified');
              queryParams.append('isVerified', 'true');
            } else if (value === 'not_verified') {
              queryParams.append('verificationStatus', 'not_verified');
              queryParams.append('isVerified', 'false');
            }
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    // Sorting
    if (params.sort) {
      queryParams.append('sortBy', params.sort.field);
      queryParams.append('sortDirection', params.sort.direction);
    }

    // DEBUG: log the final URL so you can see exactly what params are sent
    console.log('[resultsApi.getResults] query string:', queryParams.toString());

    const response = await api.get<ResultsListResponse>(`/results?${queryParams.toString()}`);
    return response.data;
  },

  getTopPerformers: async (limit: number = 10): Promise<ResultSummary[]> => {
    const response = await api.get<ApiListResponse<ResultSummary>>(`/results/top-performers?limit=${limit}`);
    return response.data.data;
  },

  getResultDetail: async (registrationNumber: string): Promise<any> => {
    const response = await api.get<ApiResponse<any>>(`/results/${registrationNumber}`);
    return response.data.data;
  },

  getDistrictStatistics: async (): Promise<any[]> => {
    const response = await api.get<ApiListResponse<any>>('/results/statistics/districts');
    return response.data.data;
  },

  exportResults: async (options: ExportOptions & { registrationNumbers?: string[] }): Promise<Blob> => {
    const response = await api.post('/results/export', options, {
      responseType: 'blob',
    });
    return response.data;
  },

  bulkOperation: async (operation: BulkOperation): Promise<{ message: string }> => {
    const response = await api.post<ApiResponse<{ message: string }>>('/results/bulk', operation);
    return response.data.data;
  },

  reprocessCandidate: async (registrationNumber: string): Promise<{
    message: string;
    registrationNumber: string;
    queuedDrawings: Array<{
      drawingId: number;
      filename: string;
      status: string;
    }>;
    totalDrawings: number;
    reprocessableDrawings: number;
    status: string;
  }> => {
    const response = await api.post<ApiResponse<any>>(`/results/${registrationNumber}/reprocess`);
    return response.data.data;
  },
};

// Drawings API endpoints
export const drawingsApi = {
  getUnmatchedDrawings: async (): Promise<any[]> => {
    const response = await api.get<ApiResponse<any[]>>('/drawings/unmatched');
    return response.data.data;
  },

  getDrawingsStatus: async (): Promise<DrawingStatusResponse> => {
    const response = await api.get<DrawingStatusResponse>('/drawings/status');
    return response.data;
  },

  checkRegistrationConflict: async (drawingId: number, registrationNumber: string): Promise<RegistrationCheckResponse> => {
    const response = await api.post<RegistrationCheckResponse>(`/drawings/${drawingId}/registration/check`, {
      registrationNumber,
    });
    return response.data;
  },

  updateDrawingRegistration: async (drawingId: number, registrationNumber: string, forceReplace?: boolean): Promise<any> => {
    const response = await api.put(`/drawings/${drawingId}/registration`, {
      registrationNumber,
      forceReplace,
    });
    return response.data;
  },

  resolveRegistrationConflict: async (drawingId: number, request: RegistrationResolveRequest): Promise<any> => {
    const response = await api.post(`/drawings/${drawingId}/registration/resolve`, request);
    return response.data;
  },

  triggerEvaluation: async (drawingId: number): Promise<{
    success: boolean;
    message: string;
    evaluationStarted: boolean;
    evaluationCompleted?: boolean;
    evaluationStatus?: string;
    score?: number;
    evaluationId?: number;
    error?: string;
  }> => {
    const response = await api.post<ApiResponse<any>>(`/processing/trigger-evaluation/${drawingId}`);
    return {
      success: response.data.success,
      message: response.data.data.message,
      evaluationStarted: response.data.data.evaluationStarted,
      evaluationCompleted: response.data.data.evaluationCompleted,
      evaluationStatus: response.data.data.evaluationStatus,
      score: response.data.data.score,
      evaluationId: response.data.data.evaluationId,
      error: response.data.data.error,
    };
  },

  updateDrawingStatus: async (drawingId: number, status: string, errorMessage?: string): Promise<any> => {
    const response = await api.put(`/drawings/${drawingId}/status`, {
      status,
      errorMessage,
    });
    return response.data;
  },

  getDrawingImage: async (drawingId: number): Promise<{
    drawingId: number;
    filename: string;
    imageUrl: string;
    filePath: string;
    fileSize?: number;
    imageWidth?: number;
    imageHeight?: number;
    fileType?: string;
  }> => {
    const response = await api.get<ApiResponse<{
      drawingId: number;
      filename: string;
      imageUrl: string;
      filePath: string;
      fileSize?: number;
      imageWidth?: number;
      imageHeight?: number;
      fileType?: string;
    }>>(`/drawings/${drawingId}/image`);
    return response.data.data;
  },

  checkRegistration: async (registrationNumber: string): Promise<any> => {
    const response = await api.get<ApiResponse<any>>(`/drawings/check-registration/${registrationNumber}`);
    return response.data.data;
  },
};

// Auth API endpoints
export const authApi = {
  login: async (username: string, password: string): Promise<{ user: any; sessionToken: string }> => {
    const response = await api.post('/auth/login', { username, password });
    if (response.data.data.sessionToken) {
      localStorage.setItem('sessionToken', response.data.data.sessionToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.data.sessionToken}`;
    }
    return response.data.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
    localStorage.removeItem('sessionToken');
    delete api.defaults.headers.common['Authorization'];
  },

  getCurrentUser: async (): Promise<any> => {
    const token = localStorage.getItem('sessionToken');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    const response = await api.get('/auth/me');
    return response.data.data;
  },

  getAllUsers: async (): Promise<any[]> => {
    const response = await api.get('/auth/users');
    return response.data.data;
  },

  createUser: async (userData: {
    username: string;
    password: string;
    email?: string;
    fullName?: string;
    role?: 'admin' | 'user';
  }): Promise<any> => {
    const response = await api.post('/auth/users', userData);
    return response.data.data;
  },

  updateUser: async (id: number, updates: {
    email?: string;
    fullName?: string;
    role?: 'admin' | 'user';
    isActive?: boolean;
    password?: string;
  }): Promise<any> => {
    const response = await api.put(`/auth/users/${id}`, updates);
    return response.data.data;
  },

  deleteUser: async (id: number): Promise<void> => {
    await api.delete(`/auth/users/${id}`);
  },

  resetPassword: async (newPassword: string): Promise<void> => {
    const response = await api.post('/auth/reset-password', { newPassword });
    return response.data;
  },
};

export default api;

// Candidates API endpoints
export const candidatesApi = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortDirection?: 'ASC' | 'DESC';
    registrationNumber?: string;
    participantName?: string;
    schoolName?: string;
    district?: string;
    state?: string;
    status?: string;
  }): Promise<{ data: any[]; pagination: any }> => {
    const response = await api.get('/candidates', { params });
    const apiResponse = response.data;

    return {
      data: apiResponse.data || [],
      pagination: {
        page: apiResponse.pagination?.currentPage || 1,
        limit: apiResponse.pagination?.itemsPerPage || 20,
        total: apiResponse.pagination?.totalItems || 0,
        totalPages: apiResponse.pagination?.totalPages || 0,
      }
    };
  },

  getByRegistration: async (registrationNumber: string): Promise<any> => {
    const response = await api.get<ApiResponse<any>>(`/candidates/${registrationNumber}`);
    return response.data;
  },

  getDrawings: async (registrationNumber: string): Promise<ApiListResponse<any>> => {
    const response = await api.get<ApiListResponse<any>>(`/candidates/${registrationNumber}/drawings`);
    return response.data;
  },

  update: async (registrationNumber: string, updates: any): Promise<any> => {
    const response = await api.put<ApiResponse<any>>(`/candidates/${registrationNumber}`, updates);
    return response.data;
  },

  getConflicts: async (): Promise<CandidateConflictsResponse> => {
    const response = await api.get<CandidateConflictsResponse>('/candidates/conflicts');
    return response.data;
  },

  resolveConflict: async (conflictId: number, resolution: ConflictResolutionRequest): Promise<{ success: boolean; message: string }> => {
    const response = await api.post<ApiResponse<{ message: string }>>(`/candidates/conflicts/${conflictId}/resolve`, resolution);
    return {
      success: response.data.success,
      message: response.data.data.message,
    };
  },

  checkRegistration: async (registrationNumber: string): Promise<CandidateRegistrationCheckResponse> => {
    const response = await api.post<CandidateRegistrationCheckResponse>('/candidates/check-registration', {
      registrationNumber,
    });
    return response.data;
  },

  create: async (candidateData: any): Promise<any> => {
    const response = await api.post('/candidates', candidateData);
    return response.data;
  },
};

// Evaluation API endpoints
export const evaluationApi = {
  getQuestions: async (): Promise<EvaluationQuestion[]> => {
    const response = await api.get<ApiListResponse<EvaluationQuestion>>('/evaluations/questions');
    return response.data.data;
  },

  createQuestion: async (question: Omit<EvaluationQuestion, 'id' | 'createdAt' | 'updatedAt'>): Promise<EvaluationQuestion> => {
    const response = await api.post<ApiResponse<EvaluationQuestion>>('/evaluations/questions', question);
    return response.data.data;
  },

  updateQuestion: async (id: string, updates: Partial<EvaluationQuestion>): Promise<EvaluationQuestion> => {
    const response = await api.put<ApiResponse<EvaluationQuestion>>(`/evaluations/questions/${id}`, updates);
    return response.data.data;
  },

  deleteQuestion: async (id: string): Promise<void> => {
    await api.delete(`/evaluations/questions/${id}`);
  },

  getParameterAssignments: async (): Promise<ParameterAssignment[]> => {
    const response = await api.get<ApiListResponse<ParameterAssignment>>('/parameter-assignments');
    return response.data.data;
  },

  createParameterAssignment: async (assignment: Omit<ParameterAssignment, 'id' | 'createdAt' | 'matchingCandidatesCount'>): Promise<ParameterAssignment> => {
    const response = await api.post<ApiResponse<ParameterAssignment>>('/parameter-assignments', assignment);
    return response.data.data;
  },

  updateParameterAssignment: async (id: string, updates: Partial<ParameterAssignment>): Promise<ParameterAssignment> => {
    const response = await api.put<ApiResponse<ParameterAssignment>>(`/parameter-assignments/${id}`, updates);
    return response.data.data;
  },

  deleteParameterAssignment: async (id: string): Promise<void> => {
    await api.delete(`/parameter-assignments/${id}`);
  },

  applyAllAssignments: async (): Promise<{ message: string; appliedCount: number }> => {
    const response = await api.post<ApiResponse<{ message: string; appliedCount: number }>>('/parameter-assignments/apply-all');
    return response.data.data;
  },

  getEvaluationStatus: async (): Promise<EvaluationStatus> => {
    const response = await api.get<ApiResponse<EvaluationStatus>>('/evaluations/status');
    return response.data.data;
  },

  getQueueStatus: async (): Promise<QueuedEvaluation[]> => {
    const response = await api.get<ApiListResponse<QueuedEvaluation>>('/evaluations/queue');
    return response.data.data;
  },

  retryEvaluation: async (evaluationId: string): Promise<{ message: string }> => {
    const response = await api.post<ApiResponse<{ message: string }>>(`/evaluations/retry/${evaluationId}`);
    return response.data.data;
  },

  processDrawing: async (drawingId: number): Promise<{ message: string }> => {
    const response = await api.post<ApiResponse<{ message: string }>>(`/evaluations/process/${drawingId}`);
    return response.data.data;
  },

  getEvaluationByDrawingId: async (drawingId: number): Promise<{
    evaluation: any;
    criteriaScores: any[];
    questionScores?: any[];
  }> => {
    const response = await api.get<ApiResponse<{
      evaluation: any;
      criteriaScores: any[];
      questionScores?: any[];
    }>>(`/evaluations/${drawingId}`);
    return response.data.data;
  },
};

// Competition API endpoints
export const competitionApi = {
  getAll: async (status?: string): Promise<any[]> => {
    const response = await api.get('/competitions', { params: { status } });
    return response.data;
  },

  getById: async (id: number): Promise<any> => {
    const response = await api.get(`/competitions/${id}`);
    return response.data;
  },

  create: async (competition: any): Promise<any> => {
    const response = await api.post('/competitions', competition);
    return response.data;
  },

  update: async (id: number, updates: any): Promise<any> => {
    const response = await api.put(`/competitions/${id}`, updates);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/competitions/${id}`);
  },

  getCandidates: async (id: number): Promise<any[]> => {
    const response = await api.get(`/competitions/${id}/candidates`);
    return response.data;
  },

  addCandidate: async (id: number, registrationNo: string): Promise<any> => {
    const response = await api.post(`/competitions/${id}/candidates`, { registrationNo });
    return response.data;
  },

  removeCandidate: async (id: number, registrationNo: string): Promise<void> => {
    await api.delete(`/competitions/${id}/candidates/${registrationNo}`);
  },

  getImages: async (id: number): Promise<string[]> => {
    const response = await api.get(`/competitions/${id}/images`);
    return response.data;
  },

  scanImages: async (id: number): Promise<{ importedCount: number }> => {
    const response = await api.post(`/competitions/${id}/images/scan`);
    return response.data;
  },

  scanCandidateImages: async (id: number): Promise<{ importedCount: number; processedCount: number; message: string }> => {
    const response = await api.post(`/competitions/${id}/candidates/scan`);
    return response.data;
  },

  generateResults: async (id: number): Promise<{ message: string }> => {
    const response = await api.post(`/competitions/${id}/results/generate`);
    return response.data;
  },

  getResults: async (id: number): Promise<any[]> => {
    const response = await api.get(`/competitions/${id}/results`);
    return response.data;
  },

  getTopPerformers: async (id: number, limit: number = 10): Promise<any[]> => {
    const response = await api.get(`/competitions/${id}/results/top`, { params: { limit } });
    return response.data;
  },

  getStatistics: async (id: number): Promise<any> => {
    const response = await api.get(`/competitions/${id}/statistics`);
    return response.data;
  },

  getCandidateResult: async (id: number, registrationNo: string): Promise<any> => {
    const response = await api.get(`/competitions/${id}/results/${registrationNo}`);
    return response.data;
  },
};

// Settings API endpoints
export const settingsApi = {
  getAll: async (): Promise<any[]> => {
    const response = await api.get('/settings');
    return response.data.data;
  },

  getByCategory: async (category: string): Promise<any[]> => {
    const response = await api.get(`/settings/category/${category}`);
    return response.data.data;
  },

  get: async (key: string): Promise<any> => {
    const response = await api.get(`/settings/${key}`);
    return response.data.data;
  },

  update: async (key: string, value: string): Promise<any> => {
    const response = await api.put(`/settings/${key}`, { value });
    return response.data.data;
  },

  updateMultiple: async (settings: Array<{ key: string; value: string }>): Promise<any[]> => {
    const response = await api.put('/settings', { settings });
    return response.data.data;
  },

  create: async (setting: {
    key: string;
    value: string;
    description?: string;
    category?: string;
    dataType?: string;
    isSensitive?: boolean;
  }): Promise<any> => {
    const response = await api.post('/settings', setting);
    return response.data.data;
  },

  delete: async (key: string): Promise<void> => {
    await api.delete(`/settings/${key}`);
  },
};